/**
 * Papierkorb und Rueckgaengig (F-45) - Datensaetze und Regeln.
 *
 * Eine Aktion (Loeschen, Abhaken, Umwandeln ...) wird als Liste verdichteter
 * Aenderungen festgehalten: je betroffene Zeile der Stand vorher und nachher.
 * Daraus entsteht ein Papierkorb-Eintrag (Kopf plus Zeilen in Stuecken) oder,
 * fuer Aktionen ohne Loeschung, nur eine Meldung im Speicher.
 *
 * Wiederhergestellt wird ueber ganz normale Schreibvorgaenge - die
 * Synchronisierung bringt die Zeilen so auf jedes Geraet zurueck.
 */
import type { Daten } from '@/domain/sync/zusammenfuehren';
import type { ID, ISOInstant } from '@/domain/types';

/** So lange bleibt Geloeschtes im Papierkorb. */
export const AUFBEWAHRUNG_TAGE = 30;

/**
 * Hoechstgroesse einer Papierkorb-Zeile (UTF-8). Der Sync-Server nimmt je
 * Datensatz hoechstens 1.000.000 Zeichen Base64 an - mit Verschluesselung und
 * Base64 (x 4/3) bleibt ein Stueck von 256 KB weit darunter.
 */
export const ZEILE_MAX_BYTES = 256_000;

/**
 * Absolute Obergrenze eines Stuecks. Eine einzelne Aenderung kann groesser
 * als ZEILE_MAX_BYTES sein (sie bekommt dann ein eigenes Stueck), aber nie so
 * gross, dass der Server das ganze Paket ablehnt (1.000.000 Zeichen Base64
 * = rund 750 KB JSON, abzueglich Verschluesselung und Feld-Uhren). Waere ein
 * Stueck groesser, entsteht kein Papierkorb-Eintrag - lieber eine Aktion
 * ohne Rueckgaengig als eine dauerhaft blockierte Synchronisierung.
 */
export const ZEILE_HART_MAX_BYTES = 700_000;

export type AktionsArt = 'geloescht' | 'entfernt' | 'erledigt' | 'wieder_offen' | 'umgewandelt' | 'verschoben' | 'archiviert';

/** Eine verdichtete Aenderung an einer Zeile. */
export interface Aenderung {
  /** Tabelle */
  t: string;
  id: string;
  /** Stand vor der Aktion - fehlt: die Zeile wurde angelegt. */
  vorher?: Daten;
  /** Stand nach der Aktion - fehlt: die Zeile wurde geloescht. */
  nachher?: Daten;
  /** 1 = `vorher`/`nachher` enthalten nur die geaenderten Felder. */
  teil?: 1;
  /** 1 = die Bytes des Anhangs liegen lokal in `papierkorbDateien`. */
  datei?: 1;
}

/** Je Tabelle: wie viele Zeilen die Aktion geloescht, geaendert oder angelegt hat. */
export interface Zaehler { weg: number; geaendert: number; neu: number }

/** Warum etwas nicht zurueckkommt. */
export type Grund =
  /** Der Schluessel ist inzwischen neu belegt (z. B. Journal desselben Tags). */
  | 'schon_da'
  /** Der Eintrag braucht ein Elternteil, das fehlt (z. B. Listeneintrag ohne Liste). */
  | 'eltern_fehlen'
  /**
   * Die geaenderte Zeile selbst fehlt gerade (z. B. Meilenstein entfernt,
   * danach das Projekt geloescht). Kommt sie zurueck, geht auch das hier.
   */
  | 'fehlt'
  /** Anhang ohne Bytes auf diesem Geraet (auf einem anderen geloescht). */
  | 'datei_fehlt'
  /** Seit der Aktion veraendert - nur beim Rueckgaengigmachen angelegter Zeilen. */
  | 'geaendert';

export interface Hinweis {
  grund: Grund;
  t: string;
  id: string;
  titel: string;
  /**
   * Bei `eltern_fehlen`: das fehlende Elternteil; bei `fehlt`: die Zeile
   * selbst. `titel`, wenn sie im Papierkorb liegt („Erst „Umzug“ ...“).
   */
  eltern?: { t: string; id: string; titel?: string };
}

/** Kopf eines Papierkorb-Eintrags (synchronisiert). */
export interface PapierkorbEintrag {
  id: ID;
  geloeschtAm: ISOInstant;
  art: AktionsArt;
  /** Tabelle der Hauptzeile(n). */
  tabelle: string;
  /** Die Hauptzeilen - mehrere bei einer Mehrfachauswahl. */
  zeileIds: string[];
  titel: string;
  zaehler: Record<string, Zaehler>;
  /** Zahl der Zeilen dieses Stands - erst wenn alle da sind, ist der Eintrag vollstaendig. */
  zeilen: number;
  /** Kennung des aktuellen Zeilen-Stands; aendert sich, wenn ein Rest neu geschrieben wird. */
  stand: string;
  /** Name des Geraets, auf dem geloescht wurde (nur mit Synchronisierung). */
  geraet?: string;
  /** Was beim letzten Wiederherstellen nicht zurueckkam. */
  hinweise?: Hinweis[];
  updatedAt: ISOInstant;
}

/** Ein Stueck der Aenderungen eines Eintrags (synchronisiert, <= ZEILE_MAX_BYTES). */
export interface PapierkorbZeile {
  id: ID;
  eintragId: ID;
  /** Laufende Nummer innerhalb des Stands. */
  nr: number;
  stand: string;
  geloeschtAm: ISOInstant;
  /** `tabelle/id` jeder enthaltenen Zeile - Index fuer „liegt das im Papierkorb?“. */
  schluessel: string[];
  aenderungen: Aenderung[];
}

/** Bytes eines geloeschten Anhangs - nur lokal, nie synchronisiert. */
export interface PapierkorbDatei {
  /** = ID des Anhangs */
  id: ID;
  eintragId: ID;
  bytes: Uint8Array<ArrayBuffer>;
}

/** Was nach einer aufgezeichneten Aktion gemeldet wird (Rueckgaengig-Meldung). */
export interface AktionsMeldung {
  art: AktionsArt;
  tabelle: string;
  titel: string;
  anzahl: number;
  /** Papierkorb-Eintrag (bei Loeschungen) ... */
  eintragId?: ID;
  /** ... oder die Aenderungen im Speicher (bei sonstigen Aktionen). */
  aenderungen?: Aenderung[];
}

/**
 * Felder, die nur gemeinsam zurueckgesetzt werden: Eine Aufgabe haengt an
 * einem Projekt ODER einem Ziel. Haengt sie inzwischen woanders, bleibt sie
 * dort - sonst wuerde die Verknuepfungsregel den neuen Verweis entfernen.
 */
export const EXKLUSIV: Readonly<Record<string, readonly (readonly string[])[]>> = {
  tasks: [['projectId', 'goalId']],
  resources: [['projectId', 'goalId']],
  thoughts: [['projectId', 'goalId']],
};

/** Eintraege, die ohne ihr Elternteil nicht bestehen koennen. */
export const PFLICHT_ELTERN: Readonly<Record<string, { feld: string; tabellen: readonly string[] }>> = {
  listenEintraege: { feld: 'listeId', tabellen: ['listen'] },
  habitEntries: { feld: 'habitId', tabellen: ['habits'] },
  planEinheiten: { feld: 'planId', tabellen: ['trainingsplaene'] },
  attachments: { feld: 'resourceId', tabellen: ['resources', 'lebensmittel'] },
  terminBestaetigungen: { feld: 'eventId', tabellen: ['events'] },
  events: { feld: 'recurringEventId', tabellen: ['events'] },
};

export const ANHAENGE = 'attachments';
